/**
 * Auth configuration and the AuthManager — the only module in the app that
 * touches `localStorage` for auth values directly.
 *
 * Storage keys MUST stay in sync with the Vue ERP and the static HTML files
 * in `public/auth-sync/`. Source of truth: `constants/storageKeys.ts`.
 */
import { EMULATION_FLAG, STORAGE_KEYS } from "../constants/storageKeys";
import { SESSION_SYNC_CLEAR_DELAY_MS } from "../constants/timing";
import { logger } from "../utils/logger";

/**
 * Shared RW backend — same host the Vue ERP's `VITE_BASE_URL` points at.
 * Used as the fallback when `REACT_APP_API_URL` is missing or invalid.
 *
 * Override at deploy time by setting `REACT_APP_API_URL` to another
 * absolute origin (e.g. `https://erp-staging.rwdomain.com`) — the bare
 * host with NO `/api` suffix, since the prefix lives on each endpoint
 * path. A relative value (e.g. `/api`) is rejected — it would resolve
 * against the React dev-server origin and 404 every request.
 */
const RW_BACKEND_URL = "https://api.rwdomain.com";
const DEFAULT_VUE_APP_URL = "https://erp.rwdomain.com";

const SOURCE_TAG = "react-checking";


/**
 * Accept `REACT_APP_API_URL` only when it is an absolute http(s) URL. A
 * relative value (e.g. `/api`) is rejected — it would resolve against the
 * React origin and 404 every request. When unset/invalid we fall back to
 * `RW_BACKEND_URL` so existing deployments behave exactly as before.
 */
const isAbsoluteHttpUrl = (value: string | undefined): value is string => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

// The `/api` mount prefix lives on each endpoint path (see AUTH_CONFIG
// below and the per-feature endpoint constants), so API_BASE_URL is the
// bare origin — NEVER append `/api` here or every request doubles it.
const API_URL_OVERRIDE = process.env.REACT_APP_API_URL;

// The endpoint paths below (and per-feature constants) all carry the `/api` mount
// prefix, so the base URL must be the bare origin. Deployments sometimes set
// REACT_APP_API_URL with a trailing `/api` (or trailing slash), which doubles every
// request to `/api/api/...`. Normalize by stripping any trailing slash and a single
// trailing `/api` segment so the prefix can never double regardless of env value.
//
// Exported for its unit test: this value becomes the axios `baseURL` for every
// request in the app, so its behaviour is pinned rather than inferred.
export const normalizeApiBaseUrl = (value: string): string =>
  value.replace(/\/+$/, "").replace(/\/api$/i, "");

const RESOLVED_API_BASE_URL = normalizeApiBaseUrl(
  isAbsoluteHttpUrl(API_URL_OVERRIDE) ? API_URL_OVERRIDE : RW_BACKEND_URL,
);

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  // Surface the URL that every axios call will use, so a stale dev server
  // or a misconfigured shell env is obvious from the DevTools console.
  // Routes through `logger.info` per coding-standards §16 — no direct console.
  logger.info("[RW] API base URL:", RESOLVED_API_BASE_URL);
}

export const AUTH_CONFIG = {
  // Storage keys — re-exported for compatibility with existing imports.
  TOKEN_KEY: STORAGE_KEYS.authToken,
  USER_KEY: STORAGE_KEYS.userData,
  REFRESH_TOKEN_KEY: STORAGE_KEYS.refreshToken,
  SESSION_SYNC_KEY: STORAGE_KEYS.sessionSync,

  // API
  API_BASE_URL: RESOLVED_API_BASE_URL,

  // Vue App URL (for cross-origin redirects/probes/syncs + Vue deep-links
  // such as the purchasing vendor page).
  VUE_APP_URL: process.env.REACT_APP_VUE_URL ?? DEFAULT_VUE_APP_URL,

  // Endpoints — each carries the `/api` mount prefix; API_BASE_URL must NOT.
  LOGIN_ENDPOINT: "/api/auth/login",
  LOGOUT_ENDPOINT: "/api/auth/logout",
  REFRESH_ENDPOINT: "/api/auth/refreshtoken",
  VALIDATE_ENDPOINT: "/api/auth/validate",
} as const;

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  // Backend returns shape that varies between providers (Google SSO etc.).
  [key: string]: unknown;
}

interface SessionSyncMessage {
  action: "login" | "logout";
  timestamp: number;
  source: string;
}

export class AuthManager {
  static getToken(): string | null {
    return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  }

  static setToken(token: string): void {
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
  }

  static getUser(): User | null {
    const userStr = localStorage.getItem(AUTH_CONFIG.USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  }

  static setUser(user: User): void {
    localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, token);
  }

  /**
   * Full local-session teardown — matches Vue's `authStore.clearSessionDetails()`
   * (`RW-ERP-Service/src/stores/auth.store.ts:362-370`) exactly: a full
   * `localStorage.clear()` + `sessionStorage.clear()`, not a removal of the
   * handful of auth-specific keys. A partial clear left stale emulation
   * flags, locale, and other per-user state behind for the next sign-in on
   * the same browser; Vue has always wiped everything on logout.
   *
   * Synchronous and called BEFORE `broadcastAuthChange()` in every caller,
   * so the cross-tab `sessionSync` key it writes lands AFTER this clear,
   * not before it.
   */
  static clearAuth(): void {
    localStorage.clear();
    sessionStorage.clear();
  }

  /**
   * Store the canonical backend user record (`userDetail`) verbatim. The
   * value is the raw JSON string as it appears in the source origin's
   * localStorage — never re-serialized, so both apps hold identical bytes.
   */
  static setUserDetail(raw: string): void {
    localStorage.setItem(STORAGE_KEYS.userDetail, raw);
  }

  /**
   * Mirror the emulation state reported by the Vue origin into this
   * origin's localStorage. An inactive/absent emulation removes all four
   * keys — `clearAuth()` doesn't touch them, so without the explicit clear
   * a stale emulation from a previous session would leak into an adopted
   * session.
   */
  static applyEmulation(
    emulation: {
      active: boolean;
      emulatedUserId: string | null;
      emulatedUserName: string | null;
      emulatedUserDetail: string | null;
    } | null
  ): void {
    if (emulation?.active && emulation.emulatedUserId) {
      localStorage.setItem(STORAGE_KEYS.isEmulate, EMULATION_FLAG.ON);
      localStorage.setItem(STORAGE_KEYS.emulatedUserId, emulation.emulatedUserId);
      if (emulation.emulatedUserName) {
        localStorage.setItem(
          STORAGE_KEYS.emulatedUserName,
          emulation.emulatedUserName
        );
      }
      if (emulation.emulatedUserDetail) {
        localStorage.setItem(
          STORAGE_KEYS.emulatedUserDetail,
          emulation.emulatedUserDetail
        );
      }
      return;
    }
    localStorage.removeItem(STORAGE_KEYS.isEmulate);
    localStorage.removeItem(STORAGE_KEYS.emulatedUserId);
    localStorage.removeItem(STORAGE_KEYS.emulatedUserName);
    localStorage.removeItem(STORAGE_KEYS.emulatedUserDetail);
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  static getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Notify other tabs on the same origin that the session changed.
   * Cross-origin sync is handled separately via iframes.
   */
  static broadcastAuthChange(action: "login" | "logout"): void {
    const message: SessionSyncMessage = {
      action,
      timestamp: Date.now(),
      source: SOURCE_TAG,
    };
    localStorage.setItem(AUTH_CONFIG.SESSION_SYNC_KEY, JSON.stringify(message));
    setTimeout(() => {
      localStorage.removeItem(AUTH_CONFIG.SESSION_SYNC_KEY);
    }, SESSION_SYNC_CLEAR_DELAY_MS);
  }

  /**
   * Listen for auth changes from other tabs on the same origin.
   * Self-fired events (source === SOURCE_TAG) are ignored.
   */
  static setupStorageListener(onLogout: () => void, onLogin: () => void): void {
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key !== AUTH_CONFIG.SESSION_SYNC_KEY || !event.newValue) return;
      let message: SessionSyncMessage;
      try {
        message = JSON.parse(event.newValue) as SessionSyncMessage;
      } catch {
        return;
      }
      if (message.source === SOURCE_TAG) return;
      if (message.action === "logout") onLogout();
      else if (message.action === "login") onLogin();
    });
  }
}
